import React, { useEffect, useMemo, useRef, useState } from 'react';

const pad = (n) => (n < 10 ? `0${n}` : String(n));
const fmt = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => (s ? new Date(`${s}T00:00:00`) : null);
const sameDay = (a, b) => a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const before = (a, b) => a && b && a.getTime() < b.getTime();
const between = (d, s, e) => s && e && d.getTime() > s.getTime() && d.getTime() < e.getTime();

export default function DateRangePicker({ value, onApply, buttonLabel = 'Date Range', className = '' }) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [start, setStart] = useState(() => parse(value?.startDate || ''));
  const [end, setEnd] = useState(() => parse(value?.endDate || ''));
  const containerRef = useRef(null);

  useEffect(() => {
    setStart(parse(value?.startDate || ''));
    setEnd(parse(value?.endDate || ''));
  }, [value?.startDate, value?.endDate]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!open) return;
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [open]);

  const weeks = useMemo(() => {
    const firstDay = new Date(month.getFullYear(), month.getMonth(), 1);
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0);
    const startIdx = firstDay.getDay();
    const days = [];
    for (let i = 0; i < startIdx; i++) days.push(null);
    for (let d = 1; d <= lastDay.getDate(); d++) days.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (days.length % 7 !== 0) days.push(null);
    const out = [];
    for (let i = 0; i < days.length; i += 7) out.push(days.slice(i, i + 7));
    return out;
  }, [month]);

  const handleDayClick = (d) => {
    if (!d) return;
    if (!start || (start && end)) {
      setStart(d);
      setEnd(null);
      return;
    }
    if (start && !end) {
      if (before(d, start)) {
        setStart(d);
        return;
      }
      if (sameDay(d, start)) {
        setEnd(d);
        return;
      }
      setEnd(d);
    }
  };

  const apply = () => {
    const s = start ? fmt(start) : '';
    const e = end ? fmt(end) : '';
    onApply && onApply(s, e);
    setOpen(false);
  };

  const clear = () => {
    setStart(null);
    setEnd(null);
    onApply && onApply('', '');
    setOpen(false);
  };

  const nextMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  };
  const prevMonth = () => {
    setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  };

  const labelText = () => {
    const s = value?.startDate || '';
    const e = value?.endDate || '';
    if (s && e) return `${s} → ${e}`;
    if (s) return `From ${s}`;
    if (e) return `Until ${e}`;
    return 'Select date range';
  };

  const hasSelection = value?.startDate || value?.endDate;

  return (
    <div className="relative" ref={containerRef}>
      {/* Modern Button with Gradient Border */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`group relative px-4 py-2.5 rounded-xl bg-gradient-to-br from-white to-gray-50 border transition-all duration-300 ${hasSelection
          ? 'border-blue-400 shadow-lg shadow-blue-100'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
          } ${className}`}
      >
        <div className="flex items-center gap-3">
          {/* Calendar Icon */}
          <div className={`transition-all duration-300 ${hasSelection ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>

          {/* Label and Selected Range */}
          <div className="flex flex-col items-start">
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{buttonLabel}</span>
            <span className={`text-sm font-semibold transition-colors ${hasSelection ? 'text-blue-700' : 'text-gray-600'
              }`}>
              {labelText()}
            </span>
          </div>

          {/* Chevron Icon */}
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Modern Dropdown with Glassmorphism */}
      {open && (
        <div className="absolute z-50 mt-3 left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0 w-[calc(100vw-32px)] sm:w-96 max-w-[360px] sm:max-w-none animate-fadeIn">
          <div className="bg-white/95 backdrop-blur-xl border border-gray-200/50 rounded-2xl shadow-2xl overflow-hidden">
            {/* Header with Month Navigation */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-4">
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={prevMonth}
                  className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>

                <div className="text-center">
                  <div className="text-white/80 text-xs font-medium uppercase tracking-wider">
                    {month.toLocaleString(undefined, { year: 'numeric' })}
                  </div>
                  <div className="text-white text-lg font-bold">
                    {month.toLocaleString(undefined, { month: 'long' })}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={nextMonth}
                  className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 text-white"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Calendar Grid */}
            <div className="px-4 py-4">
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-3">
                {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                  <div key={i} className="text-center text-xs font-bold text-gray-400 uppercase">
                    {d}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-rows-6 gap-1">
                {weeks.map((w, wi) => (
                  <div key={wi} className="grid grid-cols-7 gap-1">
                    {w.map((d, di) => {
                      const isSelStart = d && start && sameDay(d, start);
                      const isSelEnd = d && end && sameDay(d, end);
                      const inRange = d && start && end && (sameDay(d, start) || sameDay(d, end) || between(d, start, end));
                      const isToday = d && sameDay(d, new Date());

                      let dayClass = 'h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-200 cursor-pointer';

                      if (!d) {
                        dayClass += ' text-transparent pointer-events-none';
                      } else if (isSelStart || isSelEnd) {
                        dayClass += ' bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg scale-105 ring-2 ring-blue-400 ring-offset-2';
                      } else if (inRange) {
                        dayClass += ' bg-blue-50 text-blue-700 hover:bg-blue-100';
                      } else if (isToday) {
                        dayClass += ' bg-gray-100 text-gray-900 font-bold hover:bg-gray-200 ring-2 ring-gray-300';
                      } else {
                        dayClass += ' text-gray-700 hover:bg-gray-100 hover:scale-105';
                      }

                      return (
                        <div key={di} className={dayClass} onClick={() => handleDayClick(d)}>
                          {d ? d.getDate() : ''}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>

            {/* Footer with Actions */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-t border-gray-200/50">
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-medium text-gray-500">
                  Selected Range
                </div>
                <div className="flex items-center gap-2 text-xs font-semibold">
                  <span className={start ? 'text-blue-700' : 'text-gray-400'}>
                    {start ? fmt(start) : 'Start date'}
                  </span>
                  <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                  <span className={end ? 'text-blue-700' : 'text-gray-400'}>
                    {end ? fmt(end) : 'End date'}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={clear}
                  className="flex-1 px-4 py-2 text-sm font-medium rounded-lg border-2 border-gray-300 text-gray-700 hover:bg-white hover:border-gray-400 transition-all duration-200"
                >
                  Clear
                </button>
                <button
                  type="button"
                  onClick={apply}
                  className="flex-1 px-4 py-2 text-sm font-bold rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  Apply Filter
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
